/**
 * Storage Adapter
 *
 * Provides a unified storage interface that works across platforms.
 * - Web: Uses localStorage
 * - React Native: Uses AsyncStorage
 *
 * @example
 * import { createStorageAdapter, setStorageAdapter } from '@prosperia/shared';
 *
 * // Web setup (in your app entry)
 * setStorageAdapter(createStorageAdapter(window.localStorage));
 *
 * // React Native setup (in your app entry)
 * import AsyncStorage from '@react-native-async-storage/async-storage';
 * setStorageAdapter(createAsyncStorageAdapter(AsyncStorage));
 */

// Default storage implementation (synchronous, for web localStorage)
let storageAdapter = {
  getItem: (key) => null,
  setItem: (key, value) => {},
  removeItem: (key) => {},
  getAllKeys: () => [],
  // Async versions for React Native compatibility
  getItemAsync: async (key) => null,
  setItemAsync: async (key, value) => {},
  removeItemAsync: async (key) => {},
  getAllKeysAsync: async () => [],
};

/**
 * Set the storage adapter to use
 * Call this at app initialization before any storage operations
 */
export function setStorageAdapter(adapter) {
  storageAdapter = adapter;
}

/**
 * Get the current storage adapter
 */
export function getStorageAdapter() {
  return storageAdapter;
}

/**
 * Create a storage adapter from a localStorage-like object (web)
 *
 * @param {Storage} storage - localStorage or sessionStorage
 */
export function createStorageAdapter(storage) {
  return {
    getItem: (key) => storage.getItem(key),
    setItem: (key, value) => storage.setItem(key, value),
    removeItem: (key) => storage.removeItem(key),
    getAllKeys: () => Object.keys(storage),
    // Async wrappers for compatibility
    getItemAsync: async (key) => storage.getItem(key),
    setItemAsync: async (key, value) => storage.setItem(key, value),
    removeItemAsync: async (key) => storage.removeItem(key),
    getAllKeysAsync: async () => Object.keys(storage),
  };
}

/**
 * Create a storage adapter from AsyncStorage (React Native)
 *
 * @param {AsyncStorage} asyncStorage - React Native AsyncStorage
 */
export function createAsyncStorageAdapter(asyncStorage) {
  return {
    // Sync versions throw - use async versions on mobile
    getItem: (key) => {
      console.warn('Synchronous getItem not supported on mobile. Use getItemAsync.');
      return null;
    },
    setItem: (key, value) => {
      console.warn('Synchronous setItem not supported on mobile. Use setItemAsync.');
    },
    removeItem: (key) => {
      console.warn('Synchronous removeItem not supported on mobile. Use removeItemAsync.');
    },
    getAllKeys: () => {
      console.warn('Synchronous getAllKeys not supported on mobile. Use getAllKeysAsync.');
      return [];
    },
    // Async versions (primary API for mobile)
    getItemAsync: (key) => asyncStorage.getItem(key),
    setItemAsync: (key, value) => asyncStorage.setItem(key, value),
    removeItemAsync: (key) => asyncStorage.removeItem(key),
    getAllKeysAsync: () => asyncStorage.getAllKeys(),
  };
}

// Convenience functions that use the current adapter
export const storage = {
  get: (key) => storageAdapter.getItem(key),
  set: (key, value) => storageAdapter.setItem(key, value),
  remove: (key) => storageAdapter.removeItem(key),
  getAllKeys: () => storageAdapter.getAllKeys(),
  getAsync: (key) => storageAdapter.getItemAsync(key),
  setAsync: (key, value) => storageAdapter.setItemAsync(key, value),
  removeAsync: (key) => storageAdapter.removeItemAsync(key),
  getAllKeysAsync: () => storageAdapter.getAllKeysAsync(),
};

export default storage;
